import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaGithub, FaPlay, FaTimes, FaArrowRight } from "react-icons/fa";
import projects from "../data/projectData.js";

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const featured = projects[0];
  const rest = projects.slice(1);

  return (
    <div className="relative text-white max-w-6xl mx-auto px-6 py-16 overflow-hidden">
      <div className="noise-overlay"></div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
        <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">04 / Projects</p>
        <h2 className="text-4xl md:text-5xl font-black tracking-tight">Things I've<br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-violet-500">built</span></h2>
        <p className="mt-4 text-gray-500 text-sm max-w-lg">Production systems with real users, real traffic, and real architectural decisions.</p>
      </motion.div>

      {/* Featured project — full width hero */}
      <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
        className="group mt-14 rounded-2xl overflow-hidden border border-gray-800/50 hover:border-gray-700/50 bg-[#0a0a2e]/60 backdrop-blur-sm transition-all duration-700 cursor-pointer"
        onClick={() => setSelectedProject(featured)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative h-64 md:h-auto overflow-hidden">
            <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a2e] via-transparent to-transparent md:block hidden"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a2e] to-transparent md:hidden"></div>
            {featured.video && (
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-xs text-white border border-white/10">
                <FaPlay className="text-[8px]" /> Watch Demo
              </div>
            )}
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <span className="text-[10px] text-blue-400 uppercase tracking-[0.3em] font-mono mb-3">Featured Project</span>
            <h3 className="text-2xl md:text-3xl font-black text-white group-hover:text-blue-300 transition-colors duration-500">{featured.title}</h3>
            <p className="mt-4 text-gray-400 text-sm leading-[1.8]">{featured.description}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {featured.techStack.map((t, i) => (
                <span key={i} className="px-2 py-0.5 text-[10px] font-mono text-gray-500 border border-gray-800/50 rounded group-hover:text-blue-400/70 group-hover:border-blue-500/20 transition-all duration-500">{t}</span>
              ))}
            </div>
            <div className="flex items-center gap-6 mt-8">
              <a href={featured.github} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                <FaGithub /> Source
              </a>
              <span className="inline-flex items-center gap-2 text-sm text-blue-400 group-hover:gap-3 transition-all duration-300">
                View project <FaArrowRight className="text-xs" />
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Other projects — grid */}
      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-800/30 rounded-xl overflow-hidden">
        {rest.map((project) => (
          <motion.div key={project.id} variants={fadeUp}
            className="group bg-[#0a0a2e]/60 backdrop-blur-sm p-0 cursor-pointer hover:bg-gray-800/30 transition-all duration-500"
            onClick={() => setSelectedProject(project)}
          >
            <div className="relative h-40 overflow-hidden">
              <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a2e] via-[#0a0a2e]/60 to-transparent"></div>
              {project.video && (
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <FaPlay className="text-[7px] text-white ml-0.5" />
                </div>
              )}
            </div>
            <div className="p-5">
              <h3 className="text-sm font-bold text-white group-hover:text-blue-300 transition-colors">{project.title}</h3>
              <p className="text-xs text-gray-600 mt-2 leading-relaxed line-clamp-2">{project.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {project.techStack.slice(0, 3).map((t, i) => (
                  <span key={i} className="px-1.5 py-0.5 text-[9px] font-mono text-gray-600 border border-gray-800/50 rounded">{t}</span>
                ))}
                {project.techStack.length > 3 && <span className="text-[9px] text-gray-700 self-center">+{project.techStack.length - 3}</span>}
              </div>
              <div className="flex items-center justify-between mt-4">
                <a href={project.github} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-gray-600 hover:text-white transition-colors">
                  <FaGithub />
                </a>
                <span className="text-[10px] text-gray-700 group-hover:text-blue-400 transition-colors font-mono">details →</span>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 flex justify-center items-center bg-black/85 backdrop-blur-md z-50 p-4"
            onClick={() => setSelectedProject(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="bg-[#0d1117] border border-gray-800/80 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-52 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-transparent to-transparent"></div>
                <button className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition" onClick={() => setSelectedProject(null)}>
                  <FaTimes className="text-xs" />
                </button>
              </div>
              <div className="p-7 -mt-10 relative">
                <h3 className="text-2xl font-black">{selectedProject.title}</h3>
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedProject.techStack.map((t, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10px] font-mono text-gray-500 border border-gray-800/50 rounded">{t}</span>
                  ))}
                </div>
                <p className="text-gray-400 mt-5 text-sm leading-[1.8]">{selectedProject.description}</p>
                {selectedProject.video && (
                  <div className="mt-6 rounded-xl overflow-hidden border border-gray-800/50">
                    <video className="w-full" controls poster={selectedProject.image}>
                      <source src={selectedProject.video} type="video/mp4" />
                    </video>
                  </div>
                )}
                <a href={selectedProject.github} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 text-sm text-gray-400 hover:text-white transition-colors border-b border-gray-800 hover:border-gray-600 pb-0.5">
                  <FaGithub /> View source code
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;
